import { Box, Button, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import type { StyledMantineProps } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/styledMantine.types';
import styled from 'styled-components';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import {
	fadeRise,
	uploadFill,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.animations';
import {
	DS_AI_MOTION,
	dsAiFocusRingStyles,
	dsAiMoveMs,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';
import { STATUS_TONE_COLORS } from '../../../../AnalysisWorkspace/components/primitives/statusTone.utils';
import type { IngestionUploadsState, UploadCardView } from '../../useIngestionUploads.types';
import { cardStaggerDelay, UPLOAD_FADE_IN_STYLES } from './UploadPanel.styles';

interface Props {
	card: UploadCardView;
	uploads: Pick<IngestionUploadsState, 'onRemove' | 'onUseLibraryCopy' | 'onUploadAnyway'>;
}

/** One file: uploading, waiting on its library copy, counted with its pages, or never completed. */
export const UploadedFileCard = ({ card, uploads }: Props) => {
	const tCapManage = useTranslationCapManage();
	const isBusy = card.state === 'preparing' || card.state === 'uploading';

	return (
		<Stack
			component="li"
			pos="relative"
			justify="center"
			gap={4}
			miw={0}
			mih="4.5rem"
			p="xs"
			bd="1px solid gray.4"
			bg="white.0"
			styles={{
				root: {
					borderRadius: '4px',
					animation: `${fadeRise} ${DS_AI_MOTION.slowMs}ms ${DS_AI_MOTION.easeOut} both`,
					'&:hover [data-remove], &:focus-within [data-remove]': { opacity: 1 },
				},
			}}
			style={{ animationDelay: cardStaggerDelay(card.staggerIndex) }}
			data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_UPLOADED_FILE_CARD}
			data-state={card.state}
			aria-busy={isBusy}
		>
			<Group
				component="span"
				gap={4}
				miw={0}
				wrap="nowrap"
				pr="md"
				fz="md"
				fw={600}
				c="dark.8"
				title={card.name}
				styles={{
					root: {
						'& span:last-child': {
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						},
					},
				}}
			>
				<Box component="span" display="inline-flex" flex="none" c="dark.0">
					<Icon variant="document-v3" size="lg" />
				</Box>
				<span>{card.name}</span>
			</Group>
			{isBusy && (
				<Box
					component="span"
					display="block"
					h="0.25rem"
					my="xs"
					bg="gray.2"
					style={{ borderRadius: '4px', overflow: 'hidden' }}
					aria-label={tCapManage('dealstack_ai.ingest.upload.uploading')}
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_UPLOAD_PROGRESS}
				>
					<UploadFill component="span" display="block" h="100%" bg="blue.6" />
				</Box>
			)}
			{card.state === 'library_match' && card.libraryMatch && (
				<>
					<Text
						span
						fz="sm"
						c="dark.4"
						styles={{ root: UPLOAD_FADE_IN_STYLES }}
						data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_UPLOAD_LIBRARY_MATCH}
					>
						{tCapManage('dealstack_ai.ingest.upload.library_match', {
							name: card.libraryMatch.name,
						})}
					</Text>
					<Group gap="xs" wrap="nowrap">
						<Button
							size="height-28"
							onClick={() => uploads.onUseLibraryCopy(card.key)}
							data-testid={
								TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_UPLOAD_USE_LIBRARY_BUTTON
							}
						>
							{tCapManage('dealstack_ai.ingest.upload.use_library_copy')}
						</Button>
						<Button
							size="height-28"
							variant="default"
							onClick={() => uploads.onUploadAnyway(card.key)}
							data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_UPLOAD_ANYWAY_BUTTON}
						>
							{tCapManage('dealstack_ai.ingest.upload.upload_anyway')}
						</Button>
					</Group>
				</>
			)}
			{card.state === 'uploaded' && (
				<Group
					component="span"
					gap={4}
					align="normal"
					styles={{ root: UPLOAD_FADE_IN_STYLES }}
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_UPLOAD_PAGES}
				>
					{card.pages.pages && (
						<StatusBadge
							{...STATUS_TONE_COLORS.neutral}
							size="xs"
							text={tCapManage(card.pages.pages.key, card.pages.pages.values)}
						/>
					)}
					{card.pages.warnings.map((warning) => (
						<StatusBadge
							key={warning.key}
							{...STATUS_TONE_COLORS.warning}
							size="xs"
							text={tCapManage(warning.key, warning.values)}
						/>
					))}
				</Group>
			)}
			{card.state === 'incomplete' && (
				<Text span fz="sm" c="dark.4" styles={{ root: UPLOAD_FADE_IN_STYLES }}>
					{tCapManage('dealstack_ai.ingest.upload.incomplete')}
				</Text>
			)}
			{!isBusy && (
				<UnstyledButton
					type="button"
					data-remove
					pos="absolute"
					top={8}
					right={8}
					w="1.25rem"
					h="1.25rem"
					p={0}
					c="dark.0"
					display="inline-flex"
					styles={{
						root: {
							alignItems: 'center',
							justifyContent: 'center',
							border: 'none',
							borderRadius: 4,
							background: 'transparent',
							cursor: 'pointer',
							// Stays in the class, not `opacity={0}`: the card's hover rule has to win over it.
							opacity: 0,
							transition: `opacity ${DS_AI_MOTION.fastMs}ms ${DS_AI_MOTION.easing}, background-color ${DS_AI_MOTION.fastMs}ms ${DS_AI_MOTION.easing}`,
							'&:hover': { background: defaultTheme.v3.colors.gray[2] },
							...dsAiFocusRingStyles(),
						},
					}}
					aria-label={tCapManage('dealstack_ai.ingest.upload.remove_file', {
						name: card.name,
					})}
					onClick={() => uploads.onRemove(card.key)}
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_UPLOAD_REMOVE_BUTTON}
				>
					<Icon variant="close-v3" size="md" />
				</UnstyledButton>
			)}
		</Stack>
	);
};

/** The upload progress fill, growing from the left edge. */
const UploadFill = styled(Box)<StyledMantineProps<'span'>>({
	transformOrigin: 'left center',
	animation: `${uploadFill} ${dsAiMoveMs(DS_AI_MOTION.loadingMs)} ${DS_AI_MOTION.easing} both`,
});
